import { Component } from "@theme/component";
import { fetchConfig, debounce, resetLoading } from "@theme/utilities";
import { morphSection, sectionRenderer } from "@theme/section-renderer";
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from "@theme/events";

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #timeout = 5000;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    this.#cartForm = this.querySelector("#cart-form");
    if (this.#cartForm instanceof HTMLFormElement) {
      this.#cartForm.addEventListener("submit", this.#handleCartFormSubmit);
    }

    this.addEventListener("click", this.#handleMinusClick, true);

    this.#validateMOQ();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    if (this.#cartForm instanceof HTMLFormElement) {
      this.#cartForm.removeEventListener("submit", this.#handleCartFormSubmit);
    }

    this.removeEventListener("click", this.#handleMinusClick, true);
  }

  /** @type {HTMLFormElement | null} */
  #cartForm = null;

  /** @type {WeakMap<HTMLElement, ReturnType<typeof setTimeout>>} */
  #moqFlashTimeouts = new WeakMap();

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: "change",
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const removeButtons = /** @type {TextComponent | undefined} */ (
      lineItemRow.querySelectorAll(".cart-items__remove-button")
    );
    removeButtons?.forEach((button) => {
      button?.classList.add("btn--loading");
    });
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line, event) {
    event?.preventDefault();

    this.updateQuantity({
      line,
      quantity: 0,
      action: "clear",
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const removeButtons = /** @type {TextComponent | undefined} */ (
      cartItemRowToRemove.querySelectorAll(".cart-items__remove-button")
    );
    removeButtons.forEach((button) => {
      button?.classList.add("btn--loading");
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    this.#disableCartItems();

    const { line, quantity } = config;

    const cartItemsComponents = document.querySelectorAll("cart-items-component");
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(","),
      sections_url: window.location.pathname,
    });

    fetch(`${FoxTheme.routes.cart_change_url}`, fetchConfig("json", { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetLoading(this);

        // Even with errors, backend may have updated cart to max available
        // Update UI and cart count if we have sections
        if (parsedResponseText.sections && parsedResponseText.sections[this.sectionId]) {
          const newSectionHTML = new DOMParser().parseFromString(
            parsedResponseText.sections[this.sectionId],
            "text/html"
          );

          // Grab the new cart item count from a hidden element
          const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
          const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

          this.dispatchEvent(
            new CartUpdateEvent({}, this.sectionId, {
              itemCount: newCartItemCount,
              source: "cart-items-component",
              sections: parsedResponseText.sections,
            })
          );

          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
          this.#validateMOQ();
        } else if (parsedResponseText.errors) {
          // No sections in error response - fetch cart.js for accurate count and quantity

          const cartSectionsData = {};
          let cartJson = null;
          const cartSectionsPromises = Array.from(sectionsToUpdate).map(async (sectionId) => {
            const sectionUrl = `${window.location.pathname.split("?")[0]}?section_id=${sectionId}`;

            const res = await fetch(sectionUrl);
            const html = await res.text();

            cartSectionsData[sectionId] = html;
          });

          const cartJsonPromises = fetch(FoxTheme.routes.cart)
            .then((res) => res.json())
            .then((data) => {
              cartJson = data;
            });

          await Promise.all([...cartSectionsPromises, cartJsonPromises]);

          cartJson["sections"] = cartSectionsData;

          this.dispatchEvent(
            new CartUpdateEvent(cartJson, "", {
              itemCount: cartJson.item_count || 0,
              sections: cartJson.sections,
            })
          );

          morphSection(this.sectionId, cartJson.sections[this.sectionId]);
          this.#validateMOQ();
        }

        /**
         * Show error message if exists (e.g. quantity exceeds available)
         * Call after morph section to avoid message disappear
         */
        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        // cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    if (event?.detail?.sourceId === this.sectionId) return;
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error("Cart item error not found");
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error("Cart item error container not found");

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove("hidden");

    setTimeout(() => {
      cartItemErrorContainer.classList.add("hidden");
    }, this.#timeout);
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      if (event?.detail?.sourceId === this.sectionId) return;
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }

    this.#validateMOQ();
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add("cart-items-disabled");
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove("cart-items-disabled");
  }

  /**
   * Validates each cart row against its MOQ (data-moq attribute).
   * Shows a persistent inline error for violating rows and toggles the checkout button.
   * @returns {boolean} True if all rows meet MOQ, false otherwise.
   */
  #validateMOQ = () => {
    const rows = this.querySelectorAll("tr.cart-items__table-row[data-key]");
    if (!rows.length) {
      this.#toggleCheckoutButton(true);
      return true;
    }

    let allValid = true;
    const totals = this.#getVariantQuantities(rows);

    rows.forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      const moq = parseInt(row.dataset.moq || "", 10);
      const qty = totals.get(this.#getVariantKey(row)) || 0;
      const containerEl = row.querySelector(".cart-items__error");
      const errorEl = containerEl instanceof HTMLElement ? containerEl.querySelector(".cart-item__error-text") : null;

      const hasMoqViolation = moq > 0 && qty > 0 && qty < moq;
      const ownsError = row.dataset.moqErrorActive === "true";

      if (hasMoqViolation) {
        allValid = false;
        if (errorEl instanceof HTMLElement && containerEl instanceof HTMLElement) {
          errorEl.textContent = row.dataset.moqError || "";
          containerEl.classList.remove("hidden");
        }
        row.dataset.moqErrorActive = "true";
      } else if (ownsError) {
        if (errorEl instanceof HTMLElement && containerEl instanceof HTMLElement) {
          errorEl.textContent = "";
          containerEl.classList.add("hidden");
        }
        delete row.dataset.moqErrorActive;
      }
    });

    this.#toggleCheckoutButton(allValid);
    return allValid;
  };

  /**
   * Returns the key that groups rows of the same variant. Shopify can split one
   * variant over several lines (e.g. Buy X Get Y discount allocation), so MOQ
   * must count the combined quantity, not the quantity per line.
   * @param {HTMLElement} row
   * @returns {string}
   */
  #getVariantKey = (row) => row.dataset.variantId || row.dataset.key || "";

  /**
   * Sums the quantity inputs per variant across all cart rows.
   * @param {NodeListOf<Element>} rows
   * @returns {Map<string, number>}
   */
  #getVariantQuantities = (rows) => {
    const totals = new Map();
    rows.forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      const input = row.querySelector(".quantity-input");
      const qty = input instanceof HTMLInputElement ? parseInt(input.value, 10) || 0 : 0;
      const key = this.#getVariantKey(row);
      totals.set(key, (totals.get(key) || 0) + qty);
    });
    return totals;
  };

  /**
   * Toggles the checkout button's disabled state based on MOQ validity.
   * Only re-enables the button if we were the ones to disable it
   * (respects server-rendered disabled state, e.g. empty cart).
   * @param {boolean} enabled
   */
  #toggleCheckoutButton = (enabled) => {
    const buttons = document.querySelectorAll("#checkout, .cart-summary__checkout-button");
    buttons.forEach((btn) => {
      if (!(btn instanceof HTMLElement)) return;
      const wasMoqBlocked = btn.dataset.moqBlocked === "true";
      if (!enabled) {
        btn.setAttribute("disabled", "disabled");
        btn.setAttribute("aria-disabled", "true");
        btn.dataset.moqBlocked = "true";
      } else if (wasMoqBlocked) {
        btn.removeAttribute("disabled");
        btn.removeAttribute("aria-disabled");
        delete btn.dataset.moqBlocked;
      }
    });
  };

  /**
   * Flashes the MOQ error on a row when the user clicks "-" but the line is
   * already at the MOQ floor. Auto-hides after the usual timeout unless a
   * persistent MOQ violation is active on the row.
   * @param {HTMLElement} row
   */
  #flashMoqError = (row) => {
    const containerEl = row.querySelector(".cart-items__error");
    const errorEl = containerEl instanceof HTMLElement ? containerEl.querySelector(".cart-item__error-text") : null;
    if (!(containerEl instanceof HTMLElement) || !(errorEl instanceof HTMLElement)) return;

    errorEl.textContent = row.dataset.moqError || "";
    containerEl.classList.remove("hidden");

    const prev = this.#moqFlashTimeouts.get(row);
    if (prev) clearTimeout(prev);
    const t = setTimeout(() => {
      if (row.dataset.moqErrorActive === "true") return;
      containerEl.classList.add("hidden");
      errorEl.textContent = "";
    }, this.#timeout);
    this.#moqFlashTimeouts.set(row, t);
  };

  /**
   * Intercepts clicks on the "-" button at the MOQ floor so we can show
   * feedback instead of firing a no-op cart update.
   * @param {Event} event
   */
  #handleMinusClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const minusBtn = target.closest(".quantity-minus");
    if (!minusBtn) return;
    const row = minusBtn.closest("tr.cart-items__table-row[data-key]");
    if (!(row instanceof HTMLElement)) return;
    const moq = parseInt(row.dataset.moq || "", 10);
    if (!(moq > 0)) return;
    const rows = this.querySelectorAll("tr.cart-items__table-row[data-key]");
    const qty = this.#getVariantQuantities(rows).get(this.#getVariantKey(row)) || 0;
    if (qty <= moq) {
      event.preventDefault();
      event.stopPropagation();
      this.#flashMoqError(row);
    }
  };

  /**
   * Intercepts the cart form submit to block checkout when MOQ is violated.
   * @param {SubmitEvent} event
   */
  #handleCartFormSubmit = (event) => {
    if (!(event.submitter instanceof HTMLElement) || event.submitter.getAttribute("name") !== "checkout") {
      return;
    }
    if (!this.#validateMOQ()) {
      event.preventDefault();
    }
  };

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error("Section id missing");

    return sectionId;
  }
}

if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}
